import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";

interface SdcSnSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (autoSdcId: number) => Promise<void>; // 選択後の処理をPromiseに対応
}

export const SdcSnSelectionModal: React.FC<SdcSnSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const [sdcs, setSdcs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // ローディング状態
  const [processing, setProcessing] = useState<boolean>(false); // 選択後処理中

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        setLoading(true); // ローディング開始
        try {
          const jwtToken = await getJwtTokenFromIdToken();
          const response = await fetch(
            process.env.REACT_APP_API_ENDPOINT +
              "/sdcs?isRegisteredAsDoor=false",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: jwtToken,
              },
            }
          );
          const data = await response.json();
          
          // SDC出荷日のフィルタリング
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(0, 0, 0, 0);

          const filteredSdcs = data.sdcs.filter((sdc: any) => {
            // 出荷日が未設定の場合は除外
            if (!sdc.shippingDate || sdc.shippingDate === "0000-00-00 00:00:00") {
              return false;
            }

            // 出荷日が明日以降の場合は表示しない
            const shippingDate = new Date(sdc.shippingDate);
            shippingDate.setHours(0, 0, 0, 0);
            return shippingDate < tomorrow; // 明日より前（今日まで）の出荷日のみ表示
          });

          setSdcs(filteredSdcs);
          console.log("SDCSN一覧：" + JSON.stringify(filteredSdcs));
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false); // ローディング終了
        }
      };

      fetchData();
    }
  }, [open]);

  const handleSdcSelection = async (selectedSdc: {
    sdcSerialNumber: any;
    autoSdcId: number;
    shippingDate: string;
  }) => {
    setProcessing(true); // 選択後処理中
    try {
      await onSelect(selectedSdc.autoSdcId); // 選択処理
    } catch (error) {
      console.error("Error in onSelect:", error);
    } finally {
      setProcessing(false); // 処理終了
      onClose(); // モーダルを閉じる
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>SDCSN選択</DialogTitle>
      <DialogContent>
        {loading || processing ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <Autocomplete
              options={sdcs}
              getOptionLabel={(option) => option.sdcSerialNumber}
              style={{ width: 400 }}
              onChange={(event, newValue) => handleSdcSelection(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="SDCSN検索" variant="outlined" />
              )}
            />
            <List>
              {sdcs.map((sdc, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleSdcSelection(sdc)}
                >
                  <ListItemText>
                    <Typography variant="body1" noWrap>
                      {sdc.sdcSerialNumber}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {sdc.doorPlace}
                    </Typography>
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};
